import { Grid } from '@mui/material';
import { useTheme } from '@mui/system';
import React from 'react';
import { useFullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import {
  useDisplayGlobalContext,
  useDomainContext,
  useGlobalFilterContext,
  useGlobalLocaleContext,
  useGlobalSegmentationLevel1Context,
  useGlobalTimeSliderContext,
  useLatestPreferencesContext,
} from '../../../../context/contexts';
import { useManualRefetchCountHandle } from '../../../hooks';
import { DashboardPageOptions } from '../../dashboards/types';
import { ExternalChartComponentProps } from '../../data-viz-containers/common/types';
import { storyboardComponentSettings } from './storyboard-component-settings';
import { StoryBoardItemErrorBoundary } from './StoryBoardItemErrorBoundary';
import { StoryboardComponentSettings, StoryboardItemProps } from './types';

const useExternalProps = (pageOptions: DashboardPageOptions) => {
  const manualDataRefetchCountHandle = useManualRefetchCountHandle();
  const { filterTray, timeOptions, segmentationLevel1 } = pageOptions;
  const fullScreenHandle = useFullScreenHandle();
  const timeSliderHandle = useGlobalTimeSliderContext();
  const timeSliderEnabled = timeOptions?.timeSliderEnabled ?? true;
  const filterTrayEnabled = filterTray?.enabled ?? true;
  const segmentationLevel1Enabled = segmentationLevel1?.enabled ?? true;
  const [timeSliderState] = timeSliderHandle;
  const finalTimeSliderState = timeSliderEnabled ? timeSliderState : null;
  const globalFilterHandle = useGlobalFilterContext();
  const [filtersState] = globalFilterHandle;
  const finalFiltersState = filterTrayEnabled
    ? filtersState
    : { ...filtersState, items: [] };
  const globalSegmentationHandle = useGlobalSegmentationLevel1Context();
  const [segmentationState] = globalSegmentationHandle;
  const finalSegmentationState = segmentationLevel1Enabled
    ? segmentationState
    : { ...segmentationState, items: [] };
  const displayGlobalHandle = useDisplayGlobalContext();
  const [displayGlobalState] = displayGlobalHandle;
  const locale = useGlobalLocaleContext();
  const preferences = useLatestPreferencesContext();
  const externalProps: ExternalChartComponentProps = {
    manualDataRefetchCountHandle,
    timeSliderState: finalTimeSliderState,
    filtersState: finalFiltersState,
    segmentationState: finalSegmentationState,
    preferences,
    displayGlobalState,
    fullScreenHandle,
    locale,
  };
  return externalProps;
};

const StoryboardItemInner = (props: StoryboardItemProps) => {
  const { config, pageOptions } = props;
  const externalProps = useExternalProps(pageOptions);
  const location = useLocation();
  const { t } = useTranslation();
  const domain = useDomainContext();
  const componentSettings = storyboardComponentSettings.find(
    (component) => component.componentId === config.componentConfig.componentId
  ) as StoryboardComponentSettings<any, any> | undefined;
  if (!componentSettings) {
    throw new Error(
      `Component settings not found for component id: ${config.componentConfig.componentId}`
    );
  }
  const { componentConfigToVizContainerProps, VizContainerComponent } =
    componentSettings;
  const persistenceKey = `${domain}-${location.pathname}-${config.chartId}-display`;
  // Maybe the StoryboardItem shouldn't be responsible for
  // validation but rather the outer component calling it.
  const validateConfig = componentSettings.validateConfig(
    config.componentConfig,
    t
  );
  if (!validateConfig.isValid) {
    throw new Error(validateConfig.error.message);
  }
  const vizContainerProps = componentConfigToVizContainerProps(
    config.componentConfig,
    externalProps,
    persistenceKey
  );
  const theme = useTheme();
  const {
    gridColStart,
    gridColEnd,
    gridColSpan,
    gridRowStart,
    gridRowEnd,
    gridRowSpan,
  } = config.layoutConfig;
  const gridColumn =
    gridColStart || gridColEnd ? undefined : `span ${gridColSpan ?? 1}`;
  const gridRow =
    gridRowStart || gridRowEnd ? undefined : `span ${gridRowSpan ?? 1}`;
  return (
    <Grid
      item
      sx={{
        display: 'grid',
        position: 'relative', // Equivalent to relative
        gridColumnStart: gridColStart ?? 'auto',
        gridColumnEnd: gridColEnd ?? 'auto',
        gridRowStart: gridRowStart ?? 'auto',
        gridRowEnd: gridRowEnd ?? 'auto',
        gridColumn,
        gridRow,
        height: '100%',
        padding: props.layoutProps?.padding ?? 0,
        [theme.breakpoints.down('lg')]: {
          height: '40vh',
          gridColumn: 'span 1',
          gridRow: 'span 1',
          gridColumnStart: 'auto',
          gridColumnEnd: 'auto',
          gridRowStart: 'auto',
          gridRowEnd: 'auto',
        },
      }}
    >
      <VizContainerComponent {...vizContainerProps} />
    </Grid>
  );
};

export const StoryboardItem = (props: StoryboardItemProps) => {
  return (
    <StoryBoardItemErrorBoundary>
      <StoryboardItemInner {...props} />
    </StoryBoardItemErrorBoundary>
  );
};
